import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { guestAPI } from '../services/guestAPI';
import { logError, logWarning } from '../utils/errorLogger';

interface GuestDisplayNameData {
  display_name: string;
  is_nickname?: boolean;
  real_name: string;
}

// Cache for guest display names
const displayNameCache = new Map<string, GuestDisplayNameData>();

export const useGuestDisplayName = (guestId: string | undefined) => {
  const [displayName, setDisplayName] = useState<string>('');
  const [isNickname, setIsNickname] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDisplayName = useCallback(async () => {
    if (!guestId) {
      setDisplayName('');
      setIsNickname(false);
      return;
    }

    // Check cache first
    const cached = displayNameCache.get(guestId);
    if (cached) {
      setDisplayName(cached.display_name);
      setIsNickname(cached.is_nickname || false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await guestAPI.getGuestDisplayName(guestId);
      
      if (response.success) {
        const data: GuestDisplayNameData = {
          display_name: response.display_name,
          is_nickname: response.is_nickname || false,
          real_name: response.guest.real_name
        };
        
        // Cache the result
        displayNameCache.set(guestId, data);
        
        setDisplayName(response.display_name);
        setIsNickname(response.is_nickname || false);
      } else {
        throw new Error('Failed to fetch display name');
      }
    } catch (err: any) {
      logError('Error fetching guest display name', err);
      setError(err.message || 'Failed to fetch display name');
      // Fallback to empty string
      setDisplayName('');
      setIsNickname(false);
    } finally {
      setLoading(false);
    }
  }, [guestId]);

  useEffect(() => {
    fetchDisplayName();
  }, [fetchDisplayName]);

  // Function to clear cache for a specific guest (useful when nicknames are updated)
  const clearCache = useCallback((id?: string) => {
    if (id) {
      displayNameCache.delete(id);
    } else {
      displayNameCache.clear();
    }
  }, []);

  // Function to refresh display name
  const refresh = useCallback(() => {
    if (guestId) {
      displayNameCache.delete(guestId);
      fetchDisplayName();
    }
  }, [guestId, fetchDisplayName]);

  return {
    displayName,
    isNickname,
    loading,
    error,
    refresh,
    clearCache
  };
};

// Hook for multiple guests
export const useGuestDisplayNames = (guestIds: string[]) => {
  const [displayNames, setDisplayNames] = useState<Map<string, GuestDisplayNameData>>(new Map());
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef<boolean>(false);
  const mountedRef = useRef<boolean>(true);
  const prevGuestIdsStringRef = useRef<string>('');
  const prevGuestIdsArrayRef = useRef<string[]>([]);

  // Memoize the guestIds with content-based comparison to prevent unnecessary re-renders
  // Compare array contents, not references, to avoid infinite loops
  // Note: guestIds is included in dependencies, but our content comparison logic
  // ensures we return the previous array reference if contents haven't changed
  const memoizedGuestIds = useMemo(() => {
    // Sort and stringify for stable comparison
    const sortedIds = [...guestIds].sort();
    const idsString = JSON.stringify(sortedIds);
    
    // If contents haven't changed, return previous array reference to maintain stability
    if (prevGuestIdsStringRef.current === idsString && prevGuestIdsArrayRef.current.length > 0) {
      return prevGuestIdsArrayRef.current;
    }
    
    // Contents changed, update refs and return new array
    prevGuestIdsStringRef.current = idsString;
    prevGuestIdsArrayRef.current = guestIds;
    return guestIds;
  }, [guestIds]);

  const fetchDisplayNames = useCallback(async (ids: string[]) => {
    if (ids.length === 0) {
      setDisplayNames(new Map());
      return;
    }

    // Prevent multiple simultaneous requests
    if (fetchingRef.current) {
      return;
    }

    try {
      fetchingRef.current = true;
      setLoading(true);
      setError(null);
      
      const promises = ids.map(async (guestId) => {
        // Check cache first
        const cached = displayNameCache.get(guestId);
        if (cached) {
          return { guestId, data: cached };
        }

        try {
          const response = await guestAPI.getGuestDisplayName(guestId);
          if (response.success) {
            const data: GuestDisplayNameData = {
              display_name: response.display_name,
              is_nickname: response.is_nickname || false,
              real_name: response.guest.real_name
            };
            
            // Cache the result
            displayNameCache.set(guestId, data);
            
            return { guestId, data };
          } else {
            logWarning(`API returned success: false for guest`);
            return null;
          }
        } catch (err) {
          logError(`Error fetching display name for guest`, err);
          // Don't throw, just return null to continue with other guests
          return null;
        }
      });

      const results = await Promise.all(promises);
      const newMap = new Map<string, GuestDisplayNameData>();
      
      results.forEach(result => {
        if (result) {
          newMap.set(result.guestId, result.data);
        }
      });
      
      if (mountedRef.current) {
        setDisplayNames(newMap);
      }
    } catch (err: any) {
      logError('Error fetching guest display names', err);
      if (mountedRef.current) {
        setError(err.message || 'Failed to fetch display names');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    // Only fetch if we have guest IDs
    if (memoizedGuestIds.length > 0) {
      fetchDisplayNames(memoizedGuestIds);
    } else {
      setDisplayNames(new Map());
    }
  }, [memoizedGuestIds, fetchDisplayNames]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const getDisplayName = useCallback((guestId: string) => {
    const data = displayNames.get(guestId);
    return data ? data.display_name : null;
  }, [displayNames]);

  const isNickname = useCallback((guestId: string) => {
    const data = displayNames.get(guestId);
    return data ? (data.is_nickname || false) : false;
  }, [displayNames]);

  const clearCache = useCallback((id?: string) => {
    if (id) {
      displayNameCache.delete(id);
    } else {
      displayNameCache.clear();
    }
  }, []);

  const refresh = useCallback(() => {
    guestIds.forEach(id => displayNameCache.delete(id));
    fetchDisplayNames(guestIds);
  }, [guestIds, fetchDisplayNames]);

  return {
    displayNames,
    getDisplayName,
    isNickname,
    loading,
    error,
    refresh,
    clearCache
  };
};
